import type { Banner, Service } from '@sse/schema';
import {
  ArrowDown,
  ArrowUp,
  ImagePlus,
  Loader2,
  Pencil,
  Plus,
  Save,
  Trash2,
  X,
} from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import Markdown from 'react-markdown';
import { Card, Field, btnGhost, btnPrimary, btnSecondary, inputCls } from '../components/ui';
import { ApiError } from '../lib/api';
import { formatStamp, todayRome } from '../lib/format';
import { useSession } from '../lib/session';
import { MAX_UPLOAD_MB, mimeOf, uploadOriginal } from '../lib/upload';

type Stamp = { updatedAt?: string; updatedBy?: { email?: string } };
type BannerItem = Banner & Stamp;
type ServiceItem = Service & Stamp;
interface SiteConfig {
  settings: { alertHome?: string } & Stamp;
  banners: BannerItem[];
  services: ServiceItem[];
}
type ImageKind = 'banner_mobile' | 'banner_desktop' | 'service';

const TABS = [
  ['alert', 'Avviso in home'],
  ['banners', 'Banner'],
  ['services', 'Servizi'],
] as const;
type Tab = (typeof TABS)[number][0];

export const PLACEMENTS: Record<Banner['placement'], string> = {
  home_top: 'Home, in alto',
  home_middle: 'Home, a metà pagina',
  list: 'Elenchi di eventi',
  event: 'Pagina dell’evento',
};

const IMAGE_HINTS: Record<ImageKind, string> = {
  banner_mobile: 'Telefono: 1280×400 (16:5), ritagliata al centro',
  banner_desktop: 'Computer: 1456×180, ritagliata al centro',
  service: 'Foto 4:3 (1200×900), ritagliata al centro',
};

const REASONS: Record<string, string> = {
  too_small: 'Immagine troppo piccola per questo formato.',
  unsupported: 'Formato non riconosciuto o file danneggiato.',
  unavailable: 'Elaborazione delle immagini non disponibile in questo ambiente.',
  failed: 'Elaborazione non riuscita: riprova tra poco.',
};

const message = (e: unknown) => {
  if (e instanceof ApiError) {
    const b = e.body as { message?: string; reason?: string; issues?: Array<{ path: string }> };
    if (b.issues?.length) return `Controlla i campi: ${b.issues.map((i) => i.path).join(', ')}`;
    if (b.reason) return REASONS[b.reason] ?? b.message ?? e.message;
    return e.message;
  }
  return e instanceof Error ? e.message : String(e);
};

/** "modificato il … da …" sotto ogni elemento salvato. */
const stampText = (s: Stamp) =>
  s.updatedAt
    ? `Modificato ${formatStamp(s.updatedAt)}${s.updatedBy?.email ? ` da ${s.updatedBy.email}` : ''}`
    : '';

/**
 * Impostazioni del sito (solo admin, docs/07-admin-e-bot.md §2): avviso in home, banner propri,
 * servizi per gli organizzatori. Ogni salvataggio arriva sul sito entro un minuto circa (lo
 * stream di DynamoDB fa rigenerare le pagine all'indexer).
 */
export function Settings() {
  const { api, isAdmin } = useSession();
  const [tab, setTab] = useState<Tab>('alert');
  const [data, setData] = useState<SiteConfig | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    setError(null);
    try {
      setData(await api<SiteConfig>('/config'));
    } catch (e) {
      setError(message(e));
    }
  };
  useEffect(() => {
    if (isAdmin) void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  if (!isAdmin)
    return (
      <main className="p-6 text-muted">Le impostazioni del sito sono riservate agli admin.</main>
    );

  return (
    <main className="mx-auto max-w-[1100px] p-4 md:p-6">
      <h1 className="font-display text-h2">Impostazioni del sito</h1>
      <p className="mt-1 text-sm text-muted">
        Le modifiche arrivano sul sito entro un minuto circa.
      </p>
      <div className="mt-3 flex flex-wrap gap-1" role="tablist">
        {TABS.map(([k, label]) => (
          <button
            key={k}
            role="tab"
            aria-selected={tab === k}
            onClick={() => setTab(k)}
            className={`h-9 px-3 text-sm ${tab === k ? 'bg-fg text-page' : 'bg-surface hover:bg-surface-strong'}`}
          >
            {label}
            {k === 'banners' && data ? ` (${data.banners.length})` : ''}
            {k === 'services' && data ? ` (${data.services.length})` : ''}
          </button>
        ))}
      </div>
      {error && (
        <p role="alert" className="mt-4 bg-danger-soft p-3 text-sm">
          Errore: {error}
        </p>
      )}
      {!data ? (
        <p className="mt-4 text-sm text-muted">Caricamento…</p>
      ) : (
        <div className="mt-4">
          {tab === 'alert' && (
            <AlertEditor
              settings={data.settings}
              onSaved={(settings) => setData({ ...data, settings })}
            />
          )}
          {tab === 'banners' && (
            <BannerList
              banners={data.banners}
              onChange={(banners) => setData({ ...data, banners })}
            />
          )}
          {tab === 'services' && (
            <ServiceList
              services={data.services}
              onChange={(services) => setData({ ...data, services })}
            />
          )}
        </div>
      )}
    </main>
  );
}

// --- avviso in home -------------------------------------------------------------------------

function AlertEditor({
  settings,
  onSaved,
}: {
  settings: SiteConfig['settings'];
  onSaved: (s: SiteConfig['settings']) => void;
}) {
  const { api } = useSession();
  const [text, setText] = useState(settings.alertHome ?? '');
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<{ ok: boolean; text: string } | null>(null);
  const dirty = text.trim() !== (settings.alertHome ?? '');

  const save = async (value: string) => {
    setBusy(true);
    setNotice(null);
    try {
      const r = await api<{ settings: SiteConfig['settings'] }>('/config/settings', {
        method: 'PUT',
        body: { alertHome: value },
      });
      onSaved(r.settings);
      setText(r.settings.alertHome ?? '');
      setNotice({ ok: true, text: value ? 'Avviso pubblicato.' : 'Avviso tolto dalla home.' });
    } catch (e) {
      setNotice({ ok: false, text: message(e) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Card title="Testo dell’avviso">
        <Field
          label="Avviso"
          hint="Compare in cima alla home, su sfondo giallo. Markdown: **grassetto**, [link](https://…). Vuoto = nessun avviso."
        >
          <textarea
            rows={6}
            maxLength={2000}
            value={text}
            onChange={(e) => setText(e.target.value)}
            className={`${inputCls} h-auto py-2`}
          />
        </Field>
        <div className="flex flex-wrap items-center gap-2">
          <button
            disabled={busy || !dirty}
            onClick={() => void save(text.trim())}
            className={btnPrimary}
          >
            <Save className="size-4" aria-hidden /> Salva
          </button>
          {settings.alertHome && (
            <button
              disabled={busy}
              onClick={() => window.confirm('Togliere l’avviso dalla home?') && void save('')}
              className={btnSecondary}
            >
              <Trash2 className="size-4" aria-hidden /> Togli l’avviso
            </button>
          )}
          <span className="text-xs text-muted">{stampText(settings)}</span>
        </div>
        {notice && (
          <p
            role={notice.ok ? 'status' : 'alert'}
            className={`p-2 text-sm ${notice.ok ? 'bg-ok-soft' : 'bg-danger-soft'}`}
          >
            {notice.text}
          </p>
        )}
      </Card>
      <Card title="Anteprima">
        {text.trim() ? (
          <div className="bg-accent px-4 py-3 text-sm [&_a]:underline [&_p]:m-0">
            <Markdown>{text}</Markdown>
          </div>
        ) : (
          <p className="text-sm text-muted">Nessun avviso in home.</p>
        )}
      </Card>
    </div>
  );
}

// --- immagini ---------------------------------------------------------------------------------

/** Caricamento di un'immagine a misura fissa: originale su S3, poi ritaglio con la Lambda media. */
function ImageField({
  label,
  kind,
  value,
  onChange,
  aspect,
}: {
  label: string;
  kind: ImageKind;
  value: string;
  onChange: (url: string) => void;
  aspect: string;
}) {
  const { api, config } = useSession();
  const media = config.mediaUrl ?? config.siteUrl;
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const pick = async (file: File) => {
    setError(null);
    const type = mimeOf(file);
    if (!type) return setError('Formati accettati: JPEG, PNG, WebP, HEIC o PDF.');
    if (file.size > MAX_UPLOAD_MB * 1024 * 1024)
      return setError(`Il file supera i ${MAX_UPLOAD_MB} MB.`);
    try {
      setBusy('Caricamento…');
      const uploadKey = await uploadOriginal(api, file, type, (pct) =>
        setBusy(`Caricamento ${pct}%`),
      );
      setBusy('Ritaglio…');
      const r = await api<{ url: string }>('/config/images', {
        method: 'POST',
        body: { uploadKey, kind },
      });
      onChange(r.url);
    } catch (e) {
      setError(message(e));
    } finally {
      setBusy(null);
    }
  };

  const src = value ? (value.startsWith('/') ? `${media}${value}` : value) : '';
  return (
    <div>
      <span className="mb-1 block text-label font-medium uppercase tracking-[0.04em] text-muted">
        {label}
      </span>
      <div className={`relative w-full overflow-hidden bg-surface ${aspect}`}>
        {src ? (
          // eslint-disable-next-line jsx-a11y/alt-text
          <img src={src} className="size-full object-cover" alt="" />
        ) : (
          <span className="absolute inset-0 flex items-center justify-center text-xs text-muted">
            Nessuna immagine
          </span>
        )}
        {busy && (
          <span className="absolute inset-0 flex items-center justify-center gap-2 bg-page/80 text-sm">
            <Loader2 className="size-4 animate-spin" aria-hidden /> {busy}
          </span>
        )}
      </div>
      <label className={`${btnGhost} mt-1 cursor-pointer`}>
        <ImagePlus className="size-4" aria-hidden /> {value ? 'Cambia immagine' : 'Carica immagine'}
        <input
          type="file"
          accept="image/jpeg,image/png,image/webp,image/heic,image/heif,application/pdf"
          className="sr-only"
          disabled={Boolean(busy)}
          onChange={(e) => {
            const f = e.target.files?.[0];
            e.target.value = '';
            if (f) void pick(f);
          }}
        />
      </label>
      <span className="block text-xs text-muted">{IMAGE_HINTS[kind]}</span>
      {error && (
        <span role="alert" className="block text-xs font-medium text-danger">
          {error}
        </span>
      )}
    </div>
  );
}

// --- banner ---------------------------------------------------------------------------------

type BannerDraft = Omit<Banner, 'id'> & { id?: string };
const emptyBanner = (): BannerDraft => ({
  placement: 'home_top',
  link: '',
  imageMobile: '',
  imageDesktop: '',
  alt: '',
  sponsored: false,
  weight: 1,
});

/** Stato di un banner oggi: attivo, programmato o scaduto. */
function bannerState(b: Banner, today: string): { label: string; cls: string } {
  if (b.endDate && b.endDate < today) return { label: 'Scaduto', cls: 'bg-surface text-muted' };
  if (b.startDate && b.startDate > today) return { label: 'Programmato', cls: 'bg-surface-strong' };
  return { label: 'Attivo', cls: 'bg-ok-soft' };
}

function BannerList({
  banners,
  onChange,
}: {
  banners: BannerItem[];
  onChange: (b: BannerItem[]) => void;
}) {
  const { api, config } = useSession();
  const media = config.mediaUrl ?? config.siteUrl;
  const [editing, setEditing] = useState<BannerDraft | null>(null);
  const [error, setError] = useState<string | null>(null);
  const today = todayRome();
  // attivi, poi programmati, poi scaduti; dentro ogni gruppo per posizione
  const rank = (b: Banner) =>
    ['Attivo', 'Programmato', 'Scaduto'].indexOf(bannerState(b, today).label);
  const sorted = [...banners].sort(
    (a, b) => rank(a) - rank(b) || a.placement.localeCompare(b.placement),
  );

  const remove = async (b: BannerItem) => {
    if (!window.confirm(`Eliminare il banner «${b.alt}»?`)) return;
    try {
      await api(`/config/banners/${b.id}`, { method: 'DELETE' });
      onChange(banners.filter((x) => x.id !== b.id));
    } catch (e) {
      setError(message(e));
    }
  };

  if (editing)
    return (
      <BannerForm
        initial={editing}
        onCancel={() => setEditing(null)}
        onSaved={(saved) => {
          onChange(
            banners.some((b) => b.id === saved.id)
              ? banners.map((b) => (b.id === saved.id ? saved : b))
              : [...banners, saved],
          );
          setEditing(null);
        }}
      />
    );

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-muted">
          Banner propri: in ogni spazio ne compare uno a rotazione (più peso = più spesso). Senza
          banner attivi, lo spazio mostra AdSense o resta vuoto.
        </p>
        <button onClick={() => setEditing(emptyBanner())} className={btnPrimary}>
          <Plus className="size-4" aria-hidden /> Nuovo banner
        </button>
      </div>
      {error && (
        <p role="alert" className="bg-danger-soft p-3 text-sm">
          Errore: {error}
        </p>
      )}
      {sorted.length === 0 ? (
        <p className="text-sm text-muted">Nessun banner.</p>
      ) : (
        <ul className="flex flex-col gap-3">
          {sorted.map((b) => {
            const st = bannerState(b, today);
            return (
              <li
                key={b.id}
                className="grid gap-3 border border-surface-strong p-3 md:grid-cols-[220px_1fr]"
              >
                <img
                  src={b.imageMobile.startsWith('/') ? `${media}${b.imageMobile}` : b.imageMobile}
                  alt=""
                  className="aspect-[16/5] w-full bg-surface object-cover"
                />
                <div className="flex flex-col gap-1 text-sm">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className={`px-2 py-0.5 text-[11px] font-medium uppercase ${st.cls}`}>
                      {st.label}
                    </span>
                    <strong className="font-medium">{b.alt}</strong>
                    {b.sponsored && <span className="text-xs text-muted">sponsorizzato</span>}
                  </div>
                  <span className="text-muted">
                    {PLACEMENTS[b.placement]} · peso {b.weight}
                    {b.startDate || b.endDate
                      ? ` · ${b.startDate ?? '…'} → ${b.endDate ?? '…'}`
                      : ' · sempre'}
                  </span>
                  <a href={b.link} target="_blank" rel="noopener" className="truncate underline">
                    {b.link}
                  </a>
                  <span className="text-xs text-muted">{stampText(b)}</span>
                  <div className="mt-1 flex gap-2">
                    <button onClick={() => setEditing(b)} className={btnSecondary}>
                      <Pencil className="size-4" aria-hidden /> Modifica
                    </button>
                    <button onClick={() => void remove(b)} className={btnGhost}>
                      <Trash2 className="size-4" aria-hidden /> Elimina
                    </button>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function BannerForm({
  initial,
  onCancel,
  onSaved,
}: {
  initial: BannerDraft;
  onCancel: () => void;
  onSaved: (b: BannerItem) => void;
}) {
  const { api, config } = useSession();
  const media = config.mediaUrl ?? config.siteUrl;
  const [b, setB] = useState<BannerDraft>(initial);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const set = <K extends keyof BannerDraft>(k: K, v: BannerDraft[K]) =>
    setB((x) => ({ ...x, [k]: v }));
  const valid =
    b.imageMobile && b.imageDesktop && /^https?:\/\/.+/.test(b.link) && b.alt.trim().length >= 3;

  const save = async () => {
    setBusy(true);
    setError(null);
    const { id, updatedAt, updatedBy, ...body } = b as BannerDraft & Stamp;
    void updatedAt;
    void updatedBy;
    const clean = {
      ...body,
      alt: body.alt.trim(),
      ...(body.startDate ? {} : { startDate: undefined }),
      ...(body.endDate ? {} : { endDate: undefined }),
    };
    try {
      const r = id
        ? await api<{ banner: BannerItem }>(`/config/banners/${id}`, { method: 'PUT', body: clean })
        : await api<{ banner: BannerItem }>('/config/banners', { method: 'POST', body: clean });
      onSaved(r.banner);
    } catch (e) {
      setError(message(e));
    } finally {
      setBusy(false);
    }
  };

  const abs = (v: string) => (v.startsWith('/') ? `${media}${v}` : v);
  return (
    <div className="grid gap-4 lg:grid-cols-[1fr_1fr]">
      <Card
        title={b.id ? 'Modifica banner' : 'Nuovo banner'}
        actions={
          <button onClick={onCancel} className={btnGhost}>
            <X className="size-4" aria-hidden /> Chiudi
          </button>
        }
      >
        <Field label="Posizione">
          <select
            value={b.placement}
            onChange={(e) => set('placement', e.target.value as Banner['placement'])}
            className={inputCls}
          >
            {Object.entries(PLACEMENTS).map(([k, l]) => (
              <option key={k} value={k}>
                {l}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Link" hint="Dove porta il clic (si apre in una nuova scheda).">
          <input
            type="url"
            value={b.link}
            onChange={(e) => set('link', e.target.value)}
            placeholder="https://"
            className={inputCls}
          />
        </Field>
        <Field label="Testo alternativo" hint="Cosa mostra l'immagine, per chi non la vede.">
          <input
            value={b.alt}
            maxLength={200}
            onChange={(e) => set('alt', e.target.value)}
            className={inputCls}
          />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Dal" hint="Vuoto = da subito">
            <input
              type="date"
              value={b.startDate ?? ''}
              onChange={(e) => set('startDate', e.target.value || undefined)}
              className={inputCls}
            />
          </Field>
          <Field label="Al" hint="Vuoto = senza scadenza">
            <input
              type="date"
              value={b.endDate ?? ''}
              min={b.startDate}
              onChange={(e) => set('endDate', e.target.value || undefined)}
              className={inputCls}
            />
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Peso" hint="Più alto = compare più spesso">
            <input
              type="number"
              min={0}
              max={100}
              value={b.weight}
              onChange={(e) => set('weight', Math.max(0, Number(e.target.value) || 0))}
              className={inputCls}
            />
          </Field>
          <label className="flex items-center gap-2 pt-6 text-sm">
            <input
              type="checkbox"
              checked={b.sponsored}
              onChange={(e) => set('sponsored', e.target.checked)}
            />
            Sponsorizzato (etichetta “Sponsorizzato” invece di “Pubblicità”)
          </label>
        </div>
        <ImageField
          label="Immagine per telefono"
          kind="banner_mobile"
          value={b.imageMobile}
          onChange={(v) => set('imageMobile', v)}
          aspect="aspect-[16/5]"
        />
        <ImageField
          label="Immagine per computer"
          kind="banner_desktop"
          value={b.imageDesktop}
          onChange={(v) => set('imageDesktop', v)}
          aspect="aspect-[1456/180]"
        />
        {error && (
          <p role="alert" className="bg-danger-soft p-2 text-sm">
            {error}
          </p>
        )}
        <div className="flex gap-2">
          <button disabled={busy || !valid} onClick={() => void save()} className={btnPrimary}>
            <Save className="size-4" aria-hidden /> Salva banner
          </button>
          <button onClick={onCancel} className={btnSecondary}>
            Annulla
          </button>
        </div>
      </Card>
      <Card title="Anteprima sul sito">
        <Preview label="Telefono" width="max-w-[360px]">
          {b.imageMobile ? (
            <img
              src={abs(b.imageMobile)}
              alt={b.alt}
              className="aspect-[16/5] w-full object-cover"
            />
          ) : (
            <Empty />
          )}
        </Preview>
        <Preview label="Computer" width="w-full">
          {b.imageDesktop ? (
            <img
              src={abs(b.imageDesktop)}
              alt={b.alt}
              className="aspect-[1456/180] w-full object-cover"
            />
          ) : (
            <Empty />
          )}
        </Preview>
      </Card>
    </div>
  );
}

function Preview({
  label,
  width,
  children,
}: {
  label: string;
  width: string;
  children: ReactNode;
}) {
  return (
    <div className={width}>
      <p className="mb-1 text-xs text-muted">{label}</p>
      <div className="border border-surface-strong bg-page p-3">
        <p className="mb-1 text-[11px] uppercase tracking-[0.06em] text-muted">Pubblicità</p>
        {children}
      </div>
    </div>
  );
}
const Empty = () => (
  <div className="flex aspect-[16/5] items-center justify-center bg-surface text-xs text-muted">
    Carica l’immagine
  </div>
);

// --- servizi --------------------------------------------------------------------------------

/** Corpo per l'API: senza id né firma dell'ultima modifica. */
function serviceBody(s: ServiceDraft & Stamp) {
  const { id, updatedAt, updatedBy, ...rest } = s;
  void id;
  void updatedAt;
  void updatedBy;
  return {
    ...rest,
    ...(rest.email ? {} : { email: undefined }),
    ...(rest.link ? {} : { link: undefined, linkLabel: undefined }),
  };
}

type ServiceDraft = Omit<Service, 'id'> & { id?: string };
const emptyService = (order: number): ServiceDraft => ({
  title: '',
  description: '',
  picture: '',
  offering: '',
  emailSubject: '',
  order,
});

function ServiceList({
  services,
  onChange,
}: {
  services: ServiceItem[];
  onChange: (s: ServiceItem[]) => void;
}) {
  const { api, config } = useSession();
  const media = config.mediaUrl ?? config.siteUrl;
  const [editing, setEditing] = useState<ServiceDraft | null>(null);
  const [error, setError] = useState<string | null>(null);
  const sorted = [...services].sort((a, b) => a.order - b.order);

  const remove = async (s: ServiceItem) => {
    if (!window.confirm(`Eliminare il servizio «${s.title}»?`)) return;
    try {
      await api(`/config/services/${s.id}`, { method: 'DELETE' });
      onChange(services.filter((x) => x.id !== s.id));
    } catch (e) {
      setError(message(e));
    }
  };

  /** Scambia l'ordine con il vicino (su o giù). */
  const move = async (i: number, dir: -1 | 1) => {
    const a = sorted[i]!;
    const b = sorted[i + dir];
    if (!b) return;
    try {
      const [ra, rb] = await Promise.all([
        api<{ service: ServiceItem }>(`/config/services/${a.id}`, {
          method: 'PUT',
          body: { ...serviceBody(a), order: b.order === a.order ? a.order + dir : b.order },
        }),
        api<{ service: ServiceItem }>(`/config/services/${b.id}`, {
          method: 'PUT',
          body: { ...serviceBody(b), order: a.order },
        }),
      ]);
      onChange(
        services.map((s) =>
          s.id === ra.service.id ? ra.service : s.id === rb.service.id ? rb.service : s,
        ),
      );
    } catch (e) {
      setError(message(e));
    }
  };

  if (editing)
    return (
      <ServiceForm
        initial={editing}
        onCancel={() => setEditing(null)}
        onSaved={(saved) => {
          onChange(
            services.some((s) => s.id === saved.id)
              ? services.map((s) => (s.id === saved.id ? saved : s))
              : [...services, saved],
          );
          setEditing(null);
        }}
      />
    );

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-muted">
          Servizi per gli organizzatori nella pagina “Servizi”, in quest’ordine.
        </p>
        <button
          onClick={() => setEditing(emptyService((sorted.at(-1)?.order ?? 0) + 1))}
          className={btnPrimary}
        >
          <Plus className="size-4" aria-hidden /> Nuovo servizio
        </button>
      </div>
      {error && (
        <p role="alert" className="bg-danger-soft p-3 text-sm">
          Errore: {error}
        </p>
      )}
      {sorted.length === 0 ? (
        <p className="text-sm text-muted">Nessun servizio.</p>
      ) : (
        <ul className="flex flex-col gap-3">
          {sorted.map((s, i) => (
            <li
              key={s.id}
              className="grid gap-3 border border-surface-strong p-3 md:grid-cols-[160px_1fr]"
            >
              <img
                src={s.picture.startsWith('/') ? `${media}${s.picture}` : s.picture}
                alt=""
                className="aspect-[4/3] w-full bg-surface object-cover"
              />
              <div className="flex flex-col gap-1 text-sm">
                <strong className="font-medium">{s.title}</strong>
                <span className="line-clamp-2 text-muted">{s.description}</span>
                <span className="text-xs text-muted">{stampText(s)}</span>
                <div className="mt-1 flex flex-wrap gap-2">
                  <button onClick={() => setEditing(s)} className={btnSecondary}>
                    <Pencil className="size-4" aria-hidden /> Modifica
                  </button>
                  <button
                    onClick={() => void move(i, -1)}
                    disabled={i === 0}
                    className={btnGhost}
                    aria-label="Sposta su"
                  >
                    <ArrowUp className="size-4" aria-hidden />
                  </button>
                  <button
                    onClick={() => void move(i, 1)}
                    disabled={i === sorted.length - 1}
                    className={btnGhost}
                    aria-label="Sposta giù"
                  >
                    <ArrowDown className="size-4" aria-hidden />
                  </button>
                  <button onClick={() => void remove(s)} className={btnGhost}>
                    <Trash2 className="size-4" aria-hidden /> Elimina
                  </button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ServiceForm({
  initial,
  onCancel,
  onSaved,
}: {
  initial: ServiceDraft;
  onCancel: () => void;
  onSaved: (s: ServiceItem) => void;
}) {
  const { api, config } = useSession();
  const media = config.mediaUrl ?? config.siteUrl;
  const [s, setS] = useState<ServiceDraft>(initial);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const set = <K extends keyof ServiceDraft>(k: K, v: ServiceDraft[K]) =>
    setS((x) => ({ ...x, [k]: v }));
  const valid =
    s.title.trim().length >= 3 &&
    s.description.trim().length >= 10 &&
    s.emailSubject.trim().length >= 3 &&
    Boolean(s.picture);

  const save = async () => {
    setBusy(true);
    setError(null);
    const id = s.id;
    const clean = serviceBody(s);
    try {
      const r = id
        ? await api<{ service: ServiceItem }>(`/config/services/${id}`, {
            method: 'PUT',
            body: clean,
          })
        : await api<{ service: ServiceItem }>('/config/services', { method: 'POST', body: clean });
      onSaved(r.service);
    } catch (e) {
      setError(message(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Card
        title={s.id ? 'Modifica servizio' : 'Nuovo servizio'}
        actions={
          <button onClick={onCancel} className={btnGhost}>
            <X className="size-4" aria-hidden /> Chiudi
          </button>
        }
      >
        <Field label="Titolo">
          <input
            value={s.title}
            maxLength={120}
            onChange={(e) => set('title', e.target.value)}
            className={inputCls}
          />
        </Field>
        <Field label="Descrizione" hint="Markdown">
          <textarea
            rows={5}
            value={s.description}
            onChange={(e) => set('description', e.target.value)}
            className={`${inputCls} h-auto py-2`}
          />
        </Field>
        <Field label="Offerta" hint="Facoltativa, in piccolo sotto la descrizione (markdown)">
          <textarea
            rows={3}
            value={s.offering}
            onChange={(e) => set('offering', e.target.value)}
            className={`${inputCls} h-auto py-2`}
          />
        </Field>
        <ImageField
          label="Foto"
          kind="service"
          value={s.picture}
          onChange={(v) => set('picture', v)}
          aspect="aspect-[4/3] max-w-[320px]"
        />
        <Field label="Oggetto dell’email" hint="Per il pulsante “Chiedi informazioni”">
          <input
            value={s.emailSubject}
            maxLength={150}
            onChange={(e) => set('emailSubject', e.target.value)}
            className={inputCls}
          />
        </Field>
        <Field label="Email per le richieste" hint="Vuoto = salentosagreeventi@gmail.com">
          <input
            type="email"
            value={s.email ?? ''}
            onChange={(e) => set('email', e.target.value || undefined)}
            className={inputCls}
          />
        </Field>
        <div className="grid grid-cols-[2fr_1fr] gap-3">
          <Field label="Sito del servizio" hint="Facoltativo">
            <input
              type="url"
              value={s.link ?? ''}
              placeholder="https://"
              onChange={(e) => set('link', e.target.value || undefined)}
              className={inputCls}
            />
          </Field>
          <Field label="Testo del pulsante">
            <input
              value={s.linkLabel ?? ''}
              maxLength={40}
              placeholder="Scopri di più"
              onChange={(e) => set('linkLabel', e.target.value || undefined)}
              className={inputCls}
            />
          </Field>
        </div>
        {error && (
          <p role="alert" className="bg-danger-soft p-2 text-sm">
            {error}
          </p>
        )}
        <div className="flex gap-2">
          <button disabled={busy || !valid} onClick={() => void save()} className={btnPrimary}>
            <Save className="size-4" aria-hidden /> Salva servizio
          </button>
          <button onClick={onCancel} className={btnSecondary}>
            Annulla
          </button>
        </div>
      </Card>
      <Card title="Anteprima">
        <section className="grid gap-4 bg-surface p-4 sm:grid-cols-5">
          {s.picture ? (
            <img
              src={s.picture.startsWith('/') ? `${media}${s.picture}` : s.picture}
              alt=""
              className="aspect-[4/3] w-full object-cover sm:col-span-2"
            />
          ) : (
            <div className="aspect-[4/3] w-full bg-surface-strong sm:col-span-2" />
          )}
          <div className="text-sm sm:col-span-3 [&_a]:underline [&_p]:mb-2">
            <h3 className="font-display text-h3">{s.title || 'Titolo'}</h3>
            <Markdown>{s.description}</Markdown>
            {s.offering && (
              <div className="text-xs">
                <Markdown>{s.offering}</Markdown>
              </div>
            )}
          </div>
        </section>
      </Card>
    </div>
  );
}
